import { useMemo } from 'react'

import { QuickGuiddeType, QuizCtaType } from 'app/types'
import { useAuth } from 'hooks'

import { getDisplayableQuiz, isQuizComplete } from './quiz-helpers'
import { useQuizStatuses } from './use-quiz-statuses'

export type VisibleQuiz = {
    quiz: QuizCtaType
    showAt: number
}

export type QuizPlaybackInfo = {
    quizzes: Array<VisibleQuiz>
    hasQuiz: boolean
    requiredQuizIds: Array<string>
    priorPassedIds: Set<string>
}

// Quiz is authenticated-only: anonymous users never see quizzes in embed.
// For now we show the quiz whenever an enabled one is present for a logged-in user — there is no
// plan-entitlement gate yet.
// @TODO(GD-19589): gate on the org's Stigg `hasQuiz` entitlement. It is not yet exposed to embed —
// `feature-quiz` must first be mapped into the servicesUsage doc (MAP_LIMITS_FROM_STIGG) and returned
// by `feature-flags-public` (like `canHideWatermarkEmbed`), then AND it into `isQuizEnabled` so quizzes
// disappear on downgrade.
export const useVisibleQuizzes = (playbook: QuickGuiddeType): QuizPlaybackInfo => {
    const { steps, slicingSuggestion } = playbook
    const { user } = useAuth()

    const hasQuizPermission = user?.isAnonymous === false

    const hasEnabledQuiz = useMemo(() => steps.some(step => step.cta?.ctaType === 'quiz' && step.cta.enabled), [steps])

    const isQuizEnabled = hasQuizPermission && hasEnabledQuiz

    const { statusByQuizId, isLoading } = useQuizStatuses(playbook.id, isQuizEnabled)

    return useMemo(() => {
        if (!isQuizEnabled || isLoading)
            return { quizzes: [], hasQuiz: false, requiredQuizIds: [], priorPassedIds: new Set<string>() }

        const slicing = slicingSuggestion ?? []
        const quizzes: Array<VisibleQuiz> = []
        const requiredQuizIds: Array<string> = []
        const priorPassedIds = new Set<string>()

        steps.forEach((step, index) => {
            if (step.cta?.ctaType !== 'quiz' || !step.cta.enabled) return

            const quiz = getDisplayableQuiz(step.cta)
            if (!quiz) return

            if (quiz.requirePassToComplete) requiredQuizIds.push(quiz.id)

            const passed = isQuizComplete(statusByQuizId[quiz.id])
            if (passed) priorPassedIds.add(quiz.id)

            const showAt = slicing[index]?.end
            if (typeof showAt === 'number' && !passed) quizzes.push({ quiz, showAt })
        })

        return { quizzes, hasQuiz: true, requiredQuizIds, priorPassedIds }
    }, [isQuizEnabled, isLoading, slicingSuggestion, steps, statusByQuizId])
}
